import * as React from 'react'
import { honeybookFixedTarget, ownValue } from '@lib/constants/honeybook'
import { HONEYBOOK_MEETING_TARGETS, HONEYBOOK_STAGE_TARGETS, type HoneybookConnectionDto, type HoneybookStage } from '@lib/dto/honeybook.dto'
import { FUNNEL_TARGET_LABELS } from '@lib/enums/funnel-stage.enum'
import { updateHoneybookConnectionInputSchema } from '@lib/input/honeybook.input'
import { Button } from '@/components/ui/button'
import { FormActions } from '@/components/ui/form-actions'
import { Select } from '@/components/ui/select'
import { useToast } from '@/components/ui/toast'
import { FormError } from '@/components/ui/form-error'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useSaveHoneybookSettings } from '@/hooks/useHoneyBook'

function SectionTitle({ title, hint }: { title: string; hint: string }) {
  return (
    <div>
      <h3 className="m-0 text-sm font-semibold text-ink">{title}</h3>
      <p className="m-0 text-sm text-ink-muted">{hint}</p>
    </div>
  )
}

/** One of HoneyBook's stages: what it counts as, fixed or chosen. */
function StageRow({ stage, choice, onChoose }: { stage: HoneybookStage; choice: string; onChoose: (choice: string) => void }) {
  const fixed = honeybookFixedTarget(stage)
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 border-b border-line py-2.5 last:border-b-0">
      <span className="min-w-0 text-sm text-ink">{stage.name}</span>
      {fixed === null ? (
        <Select size="sm" aria-label={`What “${stage.name}” counts as`} value={choice} onChange={(e) => onChoose(e.target.value)}>
          <option value="">Counts as nothing</option>
          {HONEYBOOK_STAGE_TARGETS.map((target) => (
            <option key={target} value={target}>
              {FUNNEL_TARGET_LABELS[target]}
            </option>
          ))}
        </Select>
      ) : (
        <span className="text-sm text-ink-muted">Counts as {FUNNEL_TARGET_LABELS[fixed]}</span>
      )}
    </li>
  )
}

/**
 * What the venue's HoneyBook stages and meetings mean in the pipeline
 * (docs/honeybook.plan.md, 3.3): one choice per stage the venue says the
 * meaning of, those not chosen yet first; HoneyBook's Inquiry and its booked
 * stages count on their own. Then which meetings are tours. Saved whole.
 */
export function HoneyBookMeanings({ venueId, connection }: { venueId: string; connection: HoneybookConnectionDto }) {
  const save = useSaveHoneybookSettings(venueId)
  const { toast } = useToast()
  const [stageChoices, setStageChoices] = React.useState<Record<string, string>>(() => ({ ...connection.funnelMap.stages }))
  const [meetingChoices, setMeetingChoices] = React.useState<Record<string, string>>(() => ({ ...connection.funnelMap.meetings }))
  const [error, setError] = React.useState<string | null>(null)

  const stages = [...connection.catalog.stages].sort((a, b) => a.order - b.order)
  const notMapped = stages.filter((s) => honeybookFixedTarget(s) === null && connection.funnelMap.stages[s.id] === undefined)
  const mapped = stages.filter((s) => !notMapped.includes(s))
  const meetings = Object.entries(connection.catalog.meetingTitles).sort(([, a], [, b]) => a.localeCompare(b))

  function handleSave(): void {
    // Only what HoneyBook still has, and a choice made: a stage it removed since
    // the venue mapped it drops out here rather than being sent back.
    const mappable = new Set(stages.filter((s) => honeybookFixedTarget(s) === null).map((s) => s.id))
    const chosen = (choices: Record<string, string>, keep: (key: string) => boolean) =>
      Object.fromEntries(Object.entries(choices).filter(([key, value]) => value !== '' && keep(key)))
    const parsed = updateHoneybookConnectionInputSchema.safeParse({
      funnelMap: {
        stages: chosen(stageChoices, (id) => mappable.has(id)),
        meetings: chosen(meetingChoices, (key) => Object.hasOwn(connection.catalog.meetingTitles, key)),
      },
    })
    if (!parsed.success) {
      setError('Choose what each stage counts as from the list.')
      return
    }
    setError(null)
    save.mutate(parsed.data, {
      onSuccess: () => toast('Saved.'),
      onError: (e) => setError(requestErrorMessage(e)),
    })
  }

  if (stages.length === 0 && meetings.length === 0) {
    return <p className="m-0 text-sm text-ink-muted">HoneyBook’s stages and meetings show here after its first read.</p>
  }

  const stageRow = (stage: HoneybookStage) => (
    <StageRow
      key={stage.id}
      stage={stage}
      choice={ownValue(stageChoices, stage.id) ?? ''}
      onChoose={(choice) => setStageChoices((all) => ({ ...all, [stage.id]: choice }))}
    />
  )

  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <SectionTitle
          title="What HoneyBook’s stages mean"
          hint="What a couple reaching each stage counts as in the pipeline. Every lead counts as an inquiry, and a booked project as Booked; tours come from meetings, below."
        />
        {notMapped.length > 0 ? (
          <div>
            <h4 className="m-0 text-xs font-medium uppercase tracking-wide text-ink-muted">Not mapped yet</h4>
            <ul className="m-0 list-none p-0">{notMapped.map(stageRow)}</ul>
          </div>
        ) : null}
        {mapped.length > 0 ? <ul className="m-0 list-none p-0">{mapped.map(stageRow)}</ul> : null}
      </section>

      <section className="space-y-3">
        <SectionTitle title="Meetings that are tours" hint="Which of the meetings couples book in HoneyBook count as a tour scheduled or an open house RSVP." />
        {meetings.length === 0 ? (
          <p className="m-0 text-sm text-ink-muted">No upcoming meetings have been seen in HoneyBook yet.</p>
        ) : (
          <ul className="m-0 list-none p-0">
            {meetings.map(([key, title]) => (
              <li key={key} className="flex flex-wrap items-center justify-between gap-3 border-b border-line py-2.5 last:border-b-0">
                <span className="min-w-0 text-sm text-ink">{title}</span>
                <Select
                  size="sm"
                  aria-label={`What “${title}” counts as`}
                  value={ownValue(meetingChoices, key) ?? ''}
                  onChange={(e) => {
                    const choice = e.target.value
                    setMeetingChoices((all) => ({ ...all, [key]: choice }))
                  }}
                >
                  <option value="">Not a tour</option>
                  {HONEYBOOK_MEETING_TARGETS.map((target) => (
                    <option key={target} value={target}>
                      {FUNNEL_TARGET_LABELS[target]}
                    </option>
                  ))}
                </Select>
              </li>
            ))}
          </ul>
        )}
      </section>

      {error === null ? null : <FormError>{error}</FormError>}
      <FormActions>
        <Button
          variant="outline"
          onClick={() => {
            setStageChoices({ ...connection.funnelMap.stages })
            setMeetingChoices({ ...connection.funnelMap.meetings })
            setError(null)
          }}
          disabled={save.isPending}
        >
          Discard changes
        </Button>
        <Button onClick={handleSave} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </FormActions>
    </div>
  )
}
